import { useCallback, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import WebpImage from '@r1-frontend/ui-react/components/WebpImage';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import Widget from '~/src/components/Widget';
import { multibundleLink } from '~/src/features/domru-tv/packages/components/MultiBundleCard';
import { ddmm } from '~/src/helpers/date';
import { IFullMultibundle } from '~/src/store/selectors/vas';

import * as ST from './styled';

interface IMultibundleSubscriptionsImage {
    id: number,
    alt: string,
    image: string,
    imageWebp: string,
}

interface IMultibundleData {
    subscriptionsNames: string[],
    subscriptionsImages: IMultibundleSubscriptionsImage[],
}

interface IProps {
    multibundle: IFullMultibundle,
    onBtnClick: () => void,
}

const MultibundleWidget = ({ multibundle, onBtnClick }: IProps): JSX.Element => {
    const { closeDateRa, nameVas, priceRa, priceVas, subscriptions } = multibundle;

    const router = useRouter();
    const swiperRef = useRef<HTMLInputElement | null>(null);

    const { subscriptionsNames, subscriptionsImages } = useMemo(() => {
        return subscriptions.reduce((acc: IMultibundleData, subscription) => {
            acc.subscriptionsNames.push(subscription.nameVas);
            const imgData = {
                id: subscription.socId,
                alt: subscription.nameVas,
                image: subscription.image,
                imageWebp: subscription.imageWebp,
            };
            acc.subscriptionsImages.push(imgData);

            return acc;
        }, {
            subscriptionsNames: [],
            subscriptionsImages: [],
        });
    }, [subscriptions]);

    const promoPeriodInfo = useMemo(() => closeDateRa && `промо-цена до ${ddmm(new Date(closeDateRa).toLocaleString('ru-RU'))}`, [closeDateRa]);

    const onClickHandler = useCallback(() => {
        onBtnClick();
        router.push(multibundleLink);
    }, [onBtnClick, router]);

    return (
        <Widget>
            <Widget.ImageBlock>
                <ST.ImgCarousel
                    innerRef={swiperRef}
                    autoplay
                    wrapAround
                    withoutArrows={true}
                    withoutDots={false}
                    slideWidth={352}
                    initialSlideHeight={160}
                    cellSpacing={0}
                    customDotsStyles={ST.CustomDotsStyles}
                >
                    {subscriptionsImages.map(image => (
                        <WebpImage
                            key={image.id}
                            urlWebp={image.imageWebp}
                        >
                            <ST.PackageImage
                                src={image.image}
                                alt={image.alt}
                            />
                        </WebpImage>
                    ))}
                </ST.ImgCarousel>
            </Widget.ImageBlock>
            <Widget.ContentBlock>
                {promoPeriodInfo && (
                    <ST.PromoLabel
                        textColor={COLORS.LabelsText_2}
                        bgColor={COLORS.LabelsBg_2}
                    >
                        {promoPeriodInfo}
                    </ST.PromoLabel>
                )}
                <Widget.Name>{nameVas}</Widget.Name>
                <Widget.Info>Подключено {subscriptions.length} подписки: {subscriptionsNames.join(', ')}</Widget.Info>
                <Widget.FooterBlock>
                    <ST.WidgetPrice isPromo={!!priceRa && !!promoPeriodInfo}>
                        {(priceRa && !!promoPeriodInfo) ? priceRa : priceVas} ₽/мес
                    </ST.WidgetPrice>
                    <BaseButton
                        type={btnTypes.SECONDARY}
                        size={btnSizes.SMALL}
                        text={'Подробнее'}
                        onClick={onClickHandler}
                    />
                </Widget.FooterBlock>
            </Widget.ContentBlock>
        </Widget>
    );
};

export default MultibundleWidget;
